import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const blank = { name: "", slug: "", emoji: "", image: "", enabled: true, order: 0 };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/categories?all=true").then(({ data }) => setCats(data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm({ ...blank, order: cats.length + 1 }); setModal(true); };
  const openEdit = (c) => { setEditing(c); setForm(c); setModal(true); };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (editing) await api.put(`/categories/${editing.id}`, form);
      else await api.post("/categories", form);
      toast.success(editing ? "Updated" : "Added");
      setModal(false);
      load();
    } catch { toast.error("Save failed"); }
  };

  const toggle = async (c) => { await api.put(`/categories/${c.id}`, { ...c, enabled: !c.enabled }); load(); };
  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    await api.delete(`/categories/${c.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-brand-dark">Categories</h1>
          <p className="text-brand-muted">{cats.length} categories</p>
        </div>
        <button data-testid="add-category-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-hover">
          <Plus className="h-4 w-4" /> Add Category
        </button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cats.map((c) => (
          <div key={c.id} data-testid={`admin-category-${c.slug}`} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{c.emoji}</span>
                <div>
                  <p className="font-medium text-brand-dark">{c.name}</p>
                  <p className="text-xs text-gray-400">{c.slug}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`edit-category-${c.slug}`} onClick={() => openEdit(c)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-category-${c.slug}`} onClick={() => del(c)} className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <button onClick={() => toggle(c)} className={`mt-3 rounded-full px-3 py-1 text-xs font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {c.enabled ? "Enabled" : "Disabled"}
            </button>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl text-brand-dark">{editing ? "Edit Category" : "Add Category"}</h2>
              <button onClick={() => setModal(false)} className="rounded-full p-2 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm text-gray-600">Name</label>
                <input data-testid="cf-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Emoji</label>
                  <input data-testid="cf-emoji" value={form.emoji} onChange={(e) => set("emoji", e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold" />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Order</label>
                  <input data-testid="cf-order" type="number" value={form.order} onChange={(e) => set("order", Number(e.target.value))} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600">Image URL</label>
                <input data-testid="cf-image" value={form.image} onChange={(e) => set("image", e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setModal(false)} className="rounded-full border border-gray-200 px-6 py-2.5 text-sm font-medium">Cancel</button>
              <button data-testid="cf-save" onClick={save} className="rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-hover">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
